import { useEffect, useCallback, useMemo } from 'react';

/**
 * Hook for managing app badge (shows count on app icon)
 * Updates badge when offline queue has pending items
 * 
 * @param {number} count - Count to display on badge
 * @returns {Object} Badge management
 *   - isSupported: boolean - Is Badge API supported
 *   - setBadge: function - Set badge count
 *   - clearBadge: function - Clear badge
 */
export default function useBadge(count = 0) {
  const isSupported = useMemo(() => {
    return (
      typeof navigator !== 'undefined' &&
      'setAppBadge' in navigator &&
      typeof navigator.setAppBadge === 'function'
    );
  }, []);

  const setBadge = useCallback(async (badgeCount) => {
    if (!isSupported) return;

    try {
      if (badgeCount > 0) {
        await navigator.setAppBadge(badgeCount);
      } else {
        await navigator.clearAppBadge();
      }
    } catch (error) {
      console.error('[useBadge] Failed to set badge:', error);
    }
  }, [isSupported]);

  const clearBadge = useCallback(async () => {
    if (!isSupported) return;

    try {
      await navigator.clearAppBadge();
    } catch (error) {
      console.error('[useBadge] Failed to clear badge:', error);
    }
  }, [isSupported]);

  // Auto-update badge when count changes
  useEffect(() => {
    if (isSupported) {
      setBadge(count);
    }
  }, [count, isSupported, setBadge]);

  return {
    isSupported,
    setBadge,
    clearBadge,
  };
}


